import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchForecast,
  fetchMap,
  fetchSession,
  maskMessage,
  pauseSitting,
  pingPresence,
  resetSession,
  resumeSitting,
  speakText,
  streamChat,
} from "../lib/api";
import MaslowSphere from "../components/MaslowSphere";
import SwirlingMass from "../components/SwirlingMass";
import VoiceControls from "../components/VoiceControls";
import ConsentStep from "../components/ConsentStep";
import { Eye, EyeOff, Lock, Pause, Play, RotateCcw, Send, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

export default function Interview({ session, setSession }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [live, setLive] = useState("");
  const [err, setErr] = useState("");
  const [map, setMap] = useState(null);
  const [speakOut, setSpeakOut] = useState(false);
  const [handsFree, setHandsFree] = useState(false);
  const [armSignal, setArmSignal] = useState(0);
  const [forecast, setForecast] = useState(null);
  const [waiting, setWaiting] = useState(0);
  const [interrupted, setInterrupted] = useState(false);
  const [maskNote, setMaskNote] = useState("");
  const [consent, setConsent] = useState(null);
  const scroller = useRef(null);
  const started = useRef(false);
  const audio = useRef(null);

  const refreshMap = useCallback(() => {
    fetchMap().then(setMap).catch(() => {});
    fetchForecast().then(setForecast).catch(() => {});
  }, []);

  useEffect(() => {
    if (session?.messages) {
      setMessages(session.messages);
      const last = session.messages[session.messages.length - 1];
      // an unanswered trailing answer means a previous turn died mid-flight
      if (last?.role === "user") setInterrupted(true);
    }
    refreshMap();
  }, [session?.session_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, live]);

  // presence heartbeat: typing activity is what resets the operator's stall clock
  const draftRef = useRef("");
  const typedAt = useRef(0);
  useEffect(() => {
    draftRef.current = draft;
    if (draft) typedAt.current = Date.now();
  }, [draft]);

  useEffect(() => {
    const beat = () =>
      pingPresence(Date.now() - typedAt.current < 8000, draftRef.current.length).catch(() => {});
    beat();
    const t = setInterval(beat, 10000);
    return () => clearInterval(t);
  }, []);

  const playReply = useCallback(async (text) => {
    if (!text?.trim()) return;
    try {
      const url = await speakText(text);
      audio.current?.pause();
      const el = new Audio(url);
      audio.current = el;
      await el.play();
      await new Promise((res) => {
        el.onended = res;
        el.onerror = res;
      });
    } catch (e) {
      console.warn("voice-out failed, falling back to text only", e);
    }
  }, []);

  const send = useCallback(
    async (text) => {
      if (streaming || !session?.session_id) return;
      setErr("");
      setInterrupted(false);
      setWaiting(0);
      setStreaming(true);
      setLive("");
      if (text) setMessages((m) => [...m, { id: `u${Date.now()}`, role: "user", content: text }]);
      let acc = "";
      const completed = await streamChat({
        text,
        sessionId: session.session_id,
        onPing: (s) => setWaiting(s),
        onDelta: (d) => {
          acc += d;
          setWaiting(0);
          setLive(acc);
        },
        onError: (e) => setErr(String(e)),
        onDone: (evt) => {
          setMessages((m) => [...m, { id: `a${Date.now()}`, role: "assistant", content: acc }]);
          setLive("");
          if (evt.public) setSession((s) => ({ ...s, ...evt.public }));
          // pull the canonical records so the mask control has real message ids
          fetchSession()
            .then((s) => {
              setMessages(s.messages);
              setSession(s);
            })
            .catch(() => {});
          refreshMap();
          if (speakOut) {
            // hands-free re-arms the mic only once the Cartographer has stopped talking
            playReply(acc).then(() => handsFree && setArmSignal((n) => n + 1));
          } else if (handsFree) {
            setArmSignal((n) => n + 1);
          }
        },
      });
      if (!completed) setInterrupted(true);
      setStreaming(false);
      setWaiting(0);
    },
    [session?.session_id, streaming, setSession, refreshMap, speakOut, playReply, handsFree]
  );

  // your answer is already on the server — this just re-asks for the reply
  const resume = useCallback(async () => {
    setErr("");
    setLive("");
    // the clock may have been stopped while the connection was down: a sitting the
    // subject paused is theirs to restart, one the operator paused is not
    let fresh = await fetchSession().catch(() => null);
    if (fresh?.timer?.state === "paused") {
      if (fresh.paused_by === "operator") {
        setSession(fresh);
        setErr("The operator paused this sitting. It will start again when they are ready.");
        return;
      }
      try {
        const r = await resumeSitting();
        fresh = { ...fresh, timer: r.timer, paused_by: r.paused_by };
      } catch (e) {
        setSession(fresh);
        setErr(e?.response?.data?.detail || "could not restart the clock");
        return;
      }
    }
    if (fresh) setSession(fresh);
    setInterrupted(false);
    await send("");
    const s = await fetchSession();
    setMessages(s.messages);
    setSession(s);
  }, [send, setSession]);

  useEffect(() => {
    if (!session?.session_id || started.current) return;
    if (!consent?.accepted) return;
    // a paused sitting must not be auto-started: that is the subject's call
    if (session.timer?.state === "paused") return;
    if ((session.messages || []).length === 0) {
      started.current = true;
      send("");
    }
  }, [session?.session_id, consent?.accepted, session?.timer?.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = (e) => {
    e.preventDefault();
    const t = draft.trim();
    if (!t) return;
    setDraft("");
    send(t);
  };

  const toggleMask = async (msg) => {
    setMaskNote("");
    try {
      const res = await maskMessage(msg.id, !msg.masked);
      setMessages((prev) =>
        prev.map((x) => (x.id === msg.id ? { ...x, masked: res.masked } : x))
      );
      setMaskNote(
        res.quotes_removed
          ? `${res.note} ${res.quotes_removed} quoted fragment${res.quotes_removed === 1 ? "" : "s"} deleted from the operator's notes.`
          : res.note
      );
    } catch {
      setMaskNote("Could not change that — reload and try again.");
    }
  };

  const hardReset = async () => {
    if (!window.confirm("Wipe the transcript and the entire map? This cannot be undone.")) return;
    await resetSession();
    started.current = false;
    const s = await fetchSession();
    setSession(s);
    setMessages([]);
    setMap(null);
  };

  if (!consent?.accepted || consent?.must_reaccept) {
    return <ConsentStep onAccepted={setConsent} />;
  }

  const staleNotice = consent?.stale && !consent?.must_reaccept ? consent.revision : null;
  const paused = session?.timer?.state === "paused";
  const pausedByOperator = paused && session?.paused_by === "operator";

  const togglePause = async () => {
    setErr("");
    try {
      const r = paused ? await resumeSitting() : await pauseSitting();
      setSession((s) => ({ ...s, timer: r.timer, paused_by: r.paused_by }));
      // picking it back up should hand them the next question, not just restart a clock
      if (paused) {
        const last = messages[messages.length - 1];
        if (!last || last.role === "user") {
          setInterrupted(false);
          await send("");
        }
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "could not change the clock");
    }
  };

  return (
    <div className="grain relative flex min-h-0 flex-1 overflow-hidden">
      <SwirlingMass density={3000} intensity={0.3} />

      <section
        data-testid="chat-panel"
        className="relative z-10 flex min-w-0 flex-1 flex-col border-r border-[var(--line)] bg-[rgba(5,5,5,0.55)] lg:max-w-[640px]"
      >
        <div className="flex items-center justify-between border-b border-[var(--line)] px-5 py-3">
          <div>
            <p className="label">The Cartographer</p>
            <p className="mt-1 font-data text-[10px] text-zinc-600">
              one question at a time · {messages.length} turns on record
              {session?.privacy?.masked_count
                ? ` · ${session.privacy.masked_count} withheld`
                : ""}
            </p>
          </div>
          <div className="flex items-center gap-5">
            <Link
              to="/privacy"
              data-testid="interview-privacy-link"
              title="What we do with your answers"
              className="label flex items-center gap-1.5 transition-colors hover:text-zinc-200"
            >
              <ShieldCheck size={11} strokeWidth={1.6} />
              {session?.privacy?.mask_by_default ? "Privacy · masking all" : "Privacy"}
            </Link>
            <button
              onClick={togglePause}
              data-testid="session-pause-btn"
              title={paused ? "Pick up where you left off" : "Stop the clock and step away"}
              className="label flex items-center gap-1.5 transition-colors hover:text-zinc-200"
            >
              {paused ? (
                <>
                  <Play size={11} strokeWidth={1.7} /> Resume
                </>
              ) : (
                <>
                  <Pause size={11} strokeWidth={1.7} /> Pause
                </>
              )}
            </button>
            <button
              onClick={hardReset}
              data-testid="session-reset-btn"
              title="Reset engagement"
              className="label flex items-center gap-1.5 transition-colors hover:text-zinc-200"
            >
              <RotateCcw size={11} strokeWidth={1.6} /> Reset
            </button>
          </div>
        </div>

        {staleNotice && (
          <div
            data-testid="consent-stale-banner"
            className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-[var(--line)] bg-[#0b0b0e] px-5 py-3"
          >
            <p className="label">Agreement reworded · v{staleNotice.to_version}</p>
            <p className="min-w-0 flex-1 font-data text-[10px] leading-relaxed text-zinc-500">
              {staleNotice.changes[0]} Nothing you agreed to has changed in substance.
            </p>
            <Link
              to="/legal"
              data-testid="consent-stale-read"
              className="label underline decoration-[var(--line-hi)] underline-offset-4 hover:text-zinc-200"
            >
              read it →
            </Link>
          </div>
        )}

        <div
          ref={scroller}
          data-testid="chat-messages-list"
          className="thin-scroll min-h-0 flex-1 space-y-7 overflow-y-auto px-5 py-7"
        >
          {messages.length === 0 && !live && !streaming && (
            <p className="font-data text-xs text-zinc-600">Opening the protocol…</p>
          )}

          {messages.map((m) => (
            <div key={m.id} data-testid={`chat-msg-${m.role}`} className="tick-in">
              <div className="mb-2 flex items-baseline gap-4">
                <p className="label">{m.role === "user" ? "You" : "Cartographer"}</p>
                {m.role === "user" && !String(m.id).startsWith("u") && (
                  <button
                    data-testid={`mask-toggle-${m.id}`}
                    onClick={() => toggleMask(m)}
                    title={
                      m.masked
                        ? "The operator cannot see this answer"
                        : "Hide this answer from the operator"
                    }
                    className="label flex items-center gap-1.5 transition-colors hover:text-zinc-200"
                  >
                    {m.masked ? (
                      <>
                        <EyeOff size={10} strokeWidth={1.7} /> withheld · show again
                      </>
                    ) : (
                      <>
                        <Eye size={10} strokeWidth={1.7} /> hide from operator
                      </>
                    )}
                  </button>
                )}
              </div>
              <p
                className={
                  m.role === "user"
                    ? `whitespace-pre-wrap border-l pl-4 text-sm leading-relaxed ${
                        m.masked
                          ? "border-zinc-500 text-zinc-600 italic"
                          : "border-[var(--line-hi)] text-zinc-400"
                      }`
                    : "whitespace-pre-wrap text-[15px] leading-[1.75] text-zinc-100"
                }
              >
                {m.content}
              </p>
            </div>
          ))}

          {maskNote && (
            <p data-testid="mask-note" className="font-data text-[10px] leading-relaxed text-zinc-500">
              — {maskNote}
            </p>
          )}

          {(live || streaming) && (
            <div data-testid="chat-msg-streaming">
              <p className="label mb-2">
                Cartographer
                {waiting > 0 && (
                  <span className="ml-3 text-zinc-600" data-testid="chat-waiting-indicator">
                    thinking · {waiting}s
                  </span>
                )}
              </p>
              <p className="whitespace-pre-wrap text-[15px] leading-[1.75] text-zinc-100">
                {live}
                <span className="caret" />
              </p>
            </div>
          )}

          {interrupted && !streaming && !paused && (
            <div
              data-testid="chat-resume-banner"
              className="panel flex flex-wrap items-center gap-4 px-5 py-4"
            >
              <div>
                <p className="label">Connection dropped</p>
                <p className="mt-2 text-xs leading-relaxed text-zinc-400">
                  Your answer is saved. Nothing was lost — resume to get the next question.
                </p>
              </div>
              <button onClick={resume} data-testid="chat-resume-btn" className="btn-stark ml-auto">
                Resume
              </button>
            </div>
          )}

          {err && (
            <p data-testid="chat-error" className="font-data text-[11px] text-zinc-300">
              — {err}
            </p>
          )}
        </div>

        <form onSubmit={submit} className="border-t border-[var(--line)] px-5 py-4">
          {paused ? (
            <div data-testid="paused-panel" className="flex flex-wrap items-center gap-4">
              <div className="min-w-0 flex-1">
                <p className="label">
                  {pausedByOperator ? "Paused by the operator" : "Paused · the clock is stopped"}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-zinc-400">
                  {pausedByOperator
                    ? "The operator has paused this sitting. It will start again when they are ready."
                    : "Nothing is lost and nothing is counting. Your place is held exactly where you left it — come back whenever you want."}
                </p>
              </div>
              {!pausedByOperator && (
                <button
                  type="button"
                  onClick={togglePause}
                  data-testid="paused-resume-btn"
                  className="btn-stark flex items-center gap-2 !py-3"
                >
                  <Play size={11} strokeWidth={1.8} /> Pick it back up
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-end gap-3">
            <textarea
              data-testid="chat-input-textarea"
              rows={2}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) submit(e);
              }}
              placeholder="Type, or hit the mic and say it out loud."
              className="thin-scroll max-h-40 min-h-[3rem] w-full resize-none bg-transparent font-sans text-sm leading-relaxed text-zinc-100 outline-none placeholder:text-zinc-700"
            />
            <button
              type="submit"
              data-testid="chat-send-btn"
              disabled={streaming || !draft.trim()}
              className="btn-stark flex items-center gap-2"
            >
              <Send size={11} strokeWidth={1.8} />
              {streaming ? "Listening" : "Send"}
            </button>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <VoiceControls
              disabled={streaming}
              speakOut={speakOut}
              setSpeakOut={setSpeakOut}
              handsFree={handsFree}
              setHandsFree={setHandsFree}
              armSignal={armSignal}
              onTranscript={(text) => {
                setDraft("");
                send(text);
              }}
            />
            <div className="flex min-w-[160px] flex-1 items-center gap-2">
              <div className="h-[2px] flex-1 bg-[var(--line)]">
                <div
                  className="h-full bg-zinc-200 transition-all duration-700"
                  data-testid="chat-tier-progress"
                  style={{ width: `${session?.coverage?.percent || 0}%` }}
                />
              </div>
              <span className="label">{session?.coverage?.percent || 0}% mapped</span>
            </div>
          </div>

          {forecast && !forecast.gate_open && (
            <p className="mt-3 font-data text-[10px] text-zinc-600" data-testid="forecast-readout">
              ~{forecast.turns_remaining} answers to unlock at your current pace
              {forecast.turns_remaining_at_target_pace < forecast.turns_remaining &&
                ` · ~${forecast.turns_remaining_at_target_pace} if you keep giving specifics`}
              {forecast.estimated_minutes ? ` · about ${forecast.estimated_minutes} min` : ""}
            </p>
          )}
          {forecast?.gate_open && (
            <p className="mt-3 font-data text-[10px] text-zinc-200" data-testid="forecast-readout">
              Map unlocked — open Results.
            </p>
          )}
            </>
          )}
        </form>
      </section>

      <section className="relative z-10 hidden min-w-0 flex-1 lg:block">
        {map && (
          <MaslowSphere map={map} visibleTiers={[1, 2, 3, 4, 5]} onSelect={() => {}} dust={false} />
        )}
        {!session?.gate_open && (
          <div className="pointer-events-none absolute inset-0 flex items-end justify-center pb-14">
            <div className="panel max-w-sm px-6 py-5 text-center">
              <Lock size={14} className="mx-auto text-zinc-500" strokeWidth={1.4} />
              <p className="label mt-3">Map sealed</p>
              <p className="mt-3 text-xs leading-relaxed text-zinc-500">
                The shells are drawn but every node is dark. Nothing resolves until your response
                pattern is predictable to three sigma.
              </p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
